// SCROLLSPY DEL INDICE DE SECCIONES (Registrar caso; pedido del operador, 5-oct-2026).
//
// QUE PASABA. El indice marcaba como «activa» toda seccion DESPLEGADA. Las ocho nacen
// desplegadas, asi que las ocho se pintaban igual y el indice no decia donde se estaba
// trabajando. Ahora la activa es UNA: la que se esta leyendo mientras se hace scroll.
//
// COMO SE DECIDE. Se traza una «linea de lectura» a `LINEA_DE_LECTURA_PX` del borde superior
// del area que se desplaza; la seccion activa es la ultima cuyo inicio ya cruzo esa linea. Al
// llegar al final del formulario manda la ultima seccion: una seccion corta (la Declaracion)
// nunca alcanzaria la linea y el indice se quedaria en la anterior.
//
// EL CLIC MANDA. Al pulsar una entrada del indice, la activa es la pulsada desde ya, y el
// calculo se suspende mientras dura el desplazamiento suave: si no, el indice recorreria
// todas las secciones intermedias y, al final de la pagina, podria marcar otra distinta de la
// que se pidio. Lo reanuda el siguiente scroll del usuario.
import { useCallback, useEffect, useRef, useState } from 'react';

/** Distancia (px) desde el borde superior del area con scroll a la que se «lee». */
export const LINEA_DE_LECTURA_PX = 120;

// Silencio de scroll que da por terminado el desplazamiento de un clic, y espera maxima si el
// clic no llega a desplazar nada (la seccion ya estaba en su sitio).
const FIN_DEL_DESPLAZAMIENTO_MS = 150;
const ESPERA_SIN_DESPLAZAMIENTO_MS = 800;

type Posicion = { clave: string; arriba: number };

/**
 * La seccion que se esta leyendo: la ultima, en orden del documento, cuyo inicio ya cruzo la
 * linea de lectura; la primera si ninguna la cruzo; la ultima si se llego al final.
 */
export function seccionEnLectura(posiciones: Posicion[], linea: number, alFinal: boolean): string | null {
  if (!posiciones.length) return null;
  if (alFinal) return posiciones.at(-1)?.clave ?? null;
  let actual = posiciones[0].clave;
  for (const p of posiciones) {
    if (p.arriba > linea) break;
    actual = p.clave;
  }
  return actual;
}

/** Desplazamiento suave, o seco si el sistema pide reducir movimiento (WCAG 2.3.3). */
export function comportamientoDelDesplazamiento(): 'auto' | 'smooth' {
  // Guarda completa: en jsdom `matchMedia` no existe (ver `Expediente` y `LoginSimulado`).
  const reducir = typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return reducir ? 'auto' : 'smooth';
}

// El area que se desplaza es el `main` del `AppShell`, no la ventana. Se busca el primer
// ancestro con scroll vertical; sin ninguno (pruebas, otra maqueta), la ventana.
function contenedorConScroll(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const { overflowY } = window.getComputedStyle(p);
    if (overflowY === 'auto' || overflowY === 'scroll') return p;
  }
  return null;
}

/**
 * La seccion activa del indice segun el scroll, y `fijar` para el clic en el indice.
 * `claves` va en orden del documento y sin las secciones que no aplican.
 */
export function useSeccionEnLectura(
  claves: string[],
  refs: { current: Record<string, HTMLElement | null> },
): { activa: string | null; fijar: (clave: string) => void } {
  const [activa, setActiva] = useState<string | null>(claves[0] ?? null);
  // Temporizador vivo mientras dura el desplazamiento que lanzo un clic.
  const bloqueo = useRef<number | null>(null);
  const lista = claves.join(',');

  useEffect(() => {
    const orden = lista ? lista.split(',') : [];
    const primera = orden.map((k) => refs.current[k]).find(Boolean);
    if (!primera) return undefined;
    const caja = contenedorConScroll(primera);
    let cuadro = 0;

    const medir = () => {
      cuadro = 0;
      const tope = caja ? caja.getBoundingClientRect().top : 0;
      const posiciones = orden.flatMap((k) => {
        const el = refs.current[k];
        return el ? [{ clave: k, arriba: el.getBoundingClientRect().top - tope }] : [];
      });
      const alFinal = caja
        ? caja.scrollTop > 0 && caja.scrollTop + caja.clientHeight >= caja.scrollHeight - 2
        : window.scrollY > 0 && window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      const k = seccionEnLectura(posiciones, LINEA_DE_LECTURA_PX, alFinal);
      if (k) setActiva(k);
    };

    const alDesplazar = () => {
      if (bloqueo.current !== null) {
        window.clearTimeout(bloqueo.current);
        bloqueo.current = window.setTimeout(() => { bloqueo.current = null; }, FIN_DEL_DESPLAZAMIENTO_MS);
        return;
      }
      if (!cuadro) cuadro = window.requestAnimationFrame(medir);
    };

    const destino: HTMLElement | Window = caja ?? window;
    destino.addEventListener('scroll', alDesplazar, { passive: true });
    cuadro = window.requestAnimationFrame(medir);
    return () => {
      destino.removeEventListener('scroll', alDesplazar);
      if (cuadro) window.cancelAnimationFrame(cuadro);
    };
  }, [lista, refs]);

  // Al desmontar no queda ningun temporizador vivo.
  useEffect(() => () => {
    if (bloqueo.current !== null) window.clearTimeout(bloqueo.current);
  }, []);

  const fijar = useCallback((clave: string) => {
    setActiva(clave);
    if (bloqueo.current !== null) window.clearTimeout(bloqueo.current);
    bloqueo.current = window.setTimeout(() => { bloqueo.current = null; }, ESPERA_SIN_DESPLAZAMIENTO_MS);
  }, []);

  return { activa, fijar };
}
